import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const DISPLAY_MS = 2500;
const EXIT_DURATION = 0.7;

const easeOut = [0.22, 1, 0.36, 1];
const easeInOut = [0.4, 0, 0.2, 1];

const AnimatedLetters = ({ text, startDelay = 0, charDelay = 0.045, className = '' }) => (
  <span className={`inline-flex flex-wrap justify-center ${className}`} aria-hidden="true">
    {text.split('').map((char, index) => (
      <motion.span
        key={`${text}-${index}`}
        initial={{ opacity: 0, y: 10, filter: 'blur(6px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{
          delay: startDelay + index * charDelay,
          duration: 0.4,
          ease: easeOut,
        }}
        className={char === ' ' ? 'w-[0.35em]' : 'inline-block'}
      >
        {char === ' ' ? '\u00A0' : char}
      </motion.span>
    ))}
  </span>
);

const FirstVisitLoader = ({ onComplete }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);

  const name = useMemo(() => t('loader.name'), [t]);
  const subtitle = useMemo(() => t('loader.subtitle'), [t]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      const timer = setTimeout(onComplete, 400);
      return () => clearTimeout(timer);
    }

    const exitTimer = setTimeout(() => setVisible(false), DISPLAY_MS);
    return () => clearTimeout(exitTimer);
  }, [onComplete]);

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {visible && (
        <motion.div
          className="first-visit-loader fixed inset-0 z-[9999] flex items-center justify-center px-4 sm:px-6"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 1.02,
          }}
          transition={{ duration: EXIT_DURATION, ease: easeInOut }}
        >
          <div className="first-visit-loader__bg absolute inset-0" aria-hidden="true">
            <div className="first-visit-loader__gradient absolute inset-0" />
            <div className="first-visit-loader__grid absolute inset-0" />
          </div>

          <motion.div
            className="relative z-10 w-full max-w-3xl text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.65, ease: easeOut }}
          >
            <h1 className="first-visit-loader__name font-bold text-textPrimary leading-tight tracking-tight text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              <span className="sr-only">{name}</span>
              <AnimatedLetters text={name} startDelay={0.15} charDelay={0.042} />
            </h1>

            <p className="first-visit-loader__subtitle mt-3 sm:mt-4 font-medium text-secondary text-base xs:text-lg sm:text-xl md:text-2xl tracking-wide">
              <span className="sr-only">{subtitle}</span>
              <AnimatedLetters
                text={subtitle}
                startDelay={0.15 + name.length * 0.042 + 0.2}
                charDelay={0.038}
              />
            </p>

            <motion.div
              className="mx-auto mt-8 sm:mt-10 h-px w-32 sm:w-40 overflow-hidden rounded-full bg-secondary/15"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              aria-hidden="true"
            >
              <motion.div
                className="h-full origin-left rounded-full bg-secondary/80"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.2, duration: DISPLAY_MS / 1000, ease: 'linear' }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FirstVisitLoader;
